import type { ListEntity, NotificationSubCategory } from "@/backend/focus";

import {
  type CustomFeedQueryConfig,
  DEFAULT_CUSTOM_FEED_QUERY_CONFIG,
  getCustomFeedQueryConfigFromPreset,
  isFeedQueryPresetId,
  normalizeCustomFeedQueryConfig,
} from "./feed-query-presets";
import {
  BUILTIN_FEED_REGISTRY,
  createBuiltinFeedRef,
  type FeedSurfaceIconKey,
  type FeedSurfaceRef,
  type FeedSurfaceType,
  getAvailableBuiltinFeedDefinitions,
  getFeedSurfaceIconKey,
  getFeedSurfaceKey,
  getRequiredBuiltinFeedDefinitions,
  isBuiltinFeedId,
  isFeedSurfaceRef,
} from "./feed-surface";
import {
  type FocusCustomNotificationView,
  getDefaultNotificationViewOrder,
  normalizeCustomNotificationViews,
  normalizeHiddenNotificationTypes,
  type NotificationViewRef,
  sanitizeNotificationViewOrder,
} from "./notification-views";

export type FeedVisibilityPreferences = {
  hideViewed: boolean;
  hideNSFW: "hide" | "blur" | "show";
  useModeration: boolean;
};

export type FocusListMetadata = {
  iconKey?: FeedSurfaceIconKey;
};

export type FocusCustomFeedPreferenceOverrides = Partial<
  Pick<FeedVisibilityPreferences, "hideNSFW" | "useModeration">
>;

export type FocusCustomFeed = {
  id: string;
  name: string;
  description?: string;
  iconKey?: FeedSurfaceIconKey;
  query: CustomFeedQueryConfig;
  feedPreferencesOverrides?: FocusCustomFeedPreferenceOverrides;
  createdAt: string;
  updatedAt: string;
};

export type FocusExtraDataV1 = {
  version: 1;
  customNotificationViews: Record<string, FocusCustomNotificationView>;
  feedOrder: FeedSurfaceRef[];
  feedPreferences: FeedVisibilityPreferences;
  hiddenNotificationTypes: NotificationSubCategory[];
  listMetadata: Record<string, FocusListMetadata>;
  customFeeds: Record<string, FocusCustomFeed>;
  notificationViewOrder: NotificationViewRef[];
};

export type FocusPreferencesSanitizeOptions = {
  loggedIn: boolean;
  availableListIds?: Iterable<string>;
  availableCustomFeedIds?: Iterable<string>;
  availableNotificationViewIds?: Iterable<string>;
};

export type FocusSurfaceResolutionOptions = FocusPreferencesSanitizeOptions & {
  lists?: ListEntity[];
  customFeeds?: Record<string, FocusCustomFeed>;
};

export type FocusSurfaceResolution = {
  ref: FeedSurfaceRef;
  key: string;
  label: string;
  type: FeedSurfaceType;
  iconKey: FeedSurfaceIconKey;
  list?: ListEntity;
  customFeed?: FocusCustomFeed;
};

export const DEFAULT_FEED_PREFERENCES: FeedVisibilityPreferences = {
  hideViewed: true,
  hideNSFW: "hide",
  useModeration: true,
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === "object" && !Array.isArray(value);
};

const isHideNsfwValue = (
  value: unknown,
): value is FeedVisibilityPreferences["hideNSFW"] => {
  return value === "hide" || value === "blur" || value === "show";
};

export const getDefaultFeedOrder = (loggedIn: boolean) => {
  return getAvailableBuiltinFeedDefinitions(loggedIn)
    .filter((builtin) => builtin.defaultVisible)
    .map((builtin) => createBuiltinFeedRef(builtin.id));
};

export const getDefaultFocusPreferences = (
  loggedIn: boolean,
  seedPreferences?: Partial<FeedVisibilityPreferences> | null,
): FocusExtraDataV1 => {
  return {
    version: 1,
    customNotificationViews: {},
    feedOrder: getDefaultFeedOrder(loggedIn),
    feedPreferences: normalizeFeedPreferences(seedPreferences),
    hiddenNotificationTypes: [],
    listMetadata: {},
    customFeeds: {},
    notificationViewOrder: getDefaultNotificationViewOrder(),
  };
};

export const normalizeFeedPreferences = (
  value?: Partial<FeedVisibilityPreferences> | null,
): FeedVisibilityPreferences => {
  return {
    hideViewed:
      typeof value?.hideViewed === "boolean"
        ? value.hideViewed
        : DEFAULT_FEED_PREFERENCES.hideViewed,
    hideNSFW: isHideNsfwValue(value?.hideNSFW)
      ? value.hideNSFW
      : DEFAULT_FEED_PREFERENCES.hideNSFW,
    useModeration:
      typeof value?.useModeration === "boolean"
        ? value.useModeration
        : DEFAULT_FEED_PREFERENCES.useModeration,
  };
};

export const normalizeFocusCustomFeedPreferenceOverrides = (
  value?: unknown,
): FocusCustomFeedPreferenceOverrides | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const overrides: FocusCustomFeedPreferenceOverrides = {};

  if (isHideNsfwValue(value.hideNSFW)) {
    overrides.hideNSFW = value.hideNSFW;
  }

  if (typeof value.useModeration === "boolean") {
    overrides.useModeration = value.useModeration;
  }

  return Object.keys(overrides).length > 0 ? overrides : undefined;
};

export const mergeFeedPreferencesWithOverrides = (
  basePreferences: FeedVisibilityPreferences,
  overrides?: FocusCustomFeedPreferenceOverrides,
): FeedVisibilityPreferences => {
  if (!overrides) {
    return basePreferences;
  }

  return {
    ...basePreferences,
    ...(isHideNsfwValue(overrides.hideNSFW)
      ? { hideNSFW: overrides.hideNSFW }
      : {}),
    ...(typeof overrides.useModeration === "boolean"
      ? { useModeration: overrides.useModeration }
      : {}),
  };
};

const normalizeListMetadata = (
  value: unknown,
): Record<string, FocusListMetadata> => {
  if (!isRecord(value)) {
    return {};
  }

  return Object.entries(value).reduce<Record<string, FocusListMetadata>>(
    (metadata, [listId, listValue]) => {
      if (!isRecord(listValue)) {
        return metadata;
      }

      const iconKey = getFeedSurfaceIconKey(
        typeof listValue.iconKey === "string" ? listValue.iconKey : null,
      );

      metadata[listId] = {
        iconKey,
      };

      return metadata;
    },
    {},
  );
};

const normalizeCustomFeeds = (
  value: unknown,
): Record<string, FocusCustomFeed> => {
  if (!isRecord(value)) {
    return {};
  }

  return Object.entries(value).reduce<Record<string, FocusCustomFeed>>(
    (feeds, [entryId, feedValue]) => {
      if (!isRecord(feedValue)) {
        return feeds;
      }

      const legacyFilters = isRecord(feedValue.filters)
        ? feedValue.filters
        : null;

      const id =
        typeof feedValue.id === "string" && feedValue.id.length > 0
          ? feedValue.id
          : entryId;
      const name =
        typeof feedValue.name === "string" && feedValue.name.trim().length > 0
          ? feedValue.name.trim()
          : null;

      if (!name) {
        return feeds;
      }

      const createdAt =
        typeof feedValue.createdAt === "string" &&
        feedValue.createdAt.length > 0
          ? feedValue.createdAt
          : new Date(0).toISOString();
      const updatedAt =
        typeof feedValue.updatedAt === "string" &&
        feedValue.updatedAt.length > 0
          ? feedValue.updatedAt
          : createdAt;
      const presetIdCandidate =
        typeof feedValue.presetId === "string"
          ? feedValue.presetId
          : typeof legacyFilters?.presetId === "string"
            ? legacyFilters.presetId
            : null;
      const feedPreferencesOverrides =
        normalizeFocusCustomFeedPreferenceOverrides(
          feedValue.feedPreferencesOverrides,
        ) ??
        normalizeFocusCustomFeedPreferenceOverrides(
          legacyFilters?.feedPreferencesOverrides,
        );
      const query = isRecord(feedValue.query)
        ? normalizeCustomFeedQueryConfig(feedValue.query)
        : isFeedQueryPresetId(presetIdCandidate)
          ? getCustomFeedQueryConfigFromPreset(presetIdCandidate)
          : DEFAULT_CUSTOM_FEED_QUERY_CONFIG;

      feeds[id] = {
        id,
        name,
        description:
          typeof feedValue.description === "string" &&
          feedValue.description.trim().length > 0
            ? feedValue.description.trim()
            : undefined,
        iconKey: getFeedSurfaceIconKey(
          typeof feedValue.iconKey === "string" ? feedValue.iconKey : null,
        ),
        query,
        feedPreferencesOverrides,
        createdAt,
        updatedAt,
      };

      return feeds;
    },
    {},
  );
};

export const sanitizeFeedOrder = (
  feedOrder: unknown,
  {
    loggedIn,
    availableListIds,
    availableCustomFeedIds,
  }: FocusPreferencesSanitizeOptions,
) => {
  const listIds = availableListIds ? new Set(availableListIds) : null;
  const customFeedIds = availableCustomFeedIds
    ? new Set(availableCustomFeedIds)
    : null;
  const defaults = getDefaultFeedOrder(loggedIn);
  const requiredBuiltins = getRequiredBuiltinFeedDefinitions(loggedIn).map(
    (builtin) => createBuiltinFeedRef(builtin.id),
  );

  if (!Array.isArray(feedOrder)) {
    return defaults;
  }

  const deduped = feedOrder.reduce<FeedSurfaceRef[]>((surfaces, surface) => {
    if (!isFeedSurfaceRef(surface)) {
      return surfaces;
    }

    if (surface.type === "builtin") {
      const builtin = BUILTIN_FEED_REGISTRY[surface.id];

      if (!loggedIn && builtin.loggedInOnly) {
        return surfaces;
      }
    }

    if (surface.type === "list" && listIds && !listIds.has(surface.id)) {
      return surfaces;
    }

    if (
      surface.type === "custom-feed" &&
      customFeedIds &&
      !customFeedIds.has(surface.id)
    ) {
      return surfaces;
    }

    const surfaceKey = getFeedSurfaceKey(surface);

    if (surfaces.some((entry) => getFeedSurfaceKey(entry) === surfaceKey)) {
      return surfaces;
    }

    surfaces.push(surface);
    return surfaces;
  }, []);

  const requiredBuiltinKeys = new Set(requiredBuiltins.map(getFeedSurfaceKey));
  const normalizedOrder = [
    ...requiredBuiltins,
    ...deduped.filter(
      (surface) => !requiredBuiltinKeys.has(getFeedSurfaceKey(surface)),
    ),
  ];

  return normalizedOrder.length > 0 ? normalizedOrder : defaults;
};

export const sanitizeFocusPreferences = (
  value: unknown,
  options: FocusPreferencesSanitizeOptions,
): FocusExtraDataV1 => {
  const baseValue = isRecord(value) ? value : {};
  const allowedListIds = options.availableListIds
    ? new Set(options.availableListIds)
    : null;
  const allowedCustomFeedIds = options.availableCustomFeedIds
    ? new Set(options.availableCustomFeedIds)
    : null;
  const customFeeds = normalizeCustomFeeds(baseValue.customFeeds);
  const availableCustomFeedIds = Object.keys(customFeeds);
  const customNotificationViews = normalizeCustomNotificationViews(
    baseValue.customNotificationViews,
  );
  const availableNotificationViewIds = Object.keys(customNotificationViews);
  const feedOrder = sanitizeFeedOrder(baseValue.feedOrder, {
    ...options,
    availableCustomFeedIds:
      options.availableCustomFeedIds ?? availableCustomFeedIds,
  });
  const notificationViewOrder = sanitizeNotificationViewOrder(
    baseValue.notificationViewOrder,
    customNotificationViews,
    {
      availableNotificationViewIds:
        options.availableNotificationViewIds ?? availableNotificationViewIds,
    },
  );

  const listMetadata = normalizeListMetadata(baseValue.listMetadata);
  const nextListMetadata = Object.fromEntries(
    Object.entries(listMetadata).filter(([listId]) => {
      return !allowedListIds || allowedListIds.has(listId);
    }),
  );

  const nextCustomFeeds = Object.fromEntries(
    Object.entries(customFeeds).filter(([customFeedId]) => {
      return !allowedCustomFeedIds || allowedCustomFeedIds.has(customFeedId);
    }),
  );
  const allowedNotificationViewIds = options.availableNotificationViewIds
    ? new Set(options.availableNotificationViewIds)
    : null;
  const nextCustomNotificationViews = Object.fromEntries(
    Object.entries(customNotificationViews).filter(([notificationViewId]) => {
      return (
        !allowedNotificationViewIds ||
        allowedNotificationViewIds.has(notificationViewId)
      );
    }),
  );

  return {
    version: 1,
    customNotificationViews: nextCustomNotificationViews,
    feedOrder,
    feedPreferences: normalizeFeedPreferences(
      isRecord(baseValue.feedPreferences)
        ? (baseValue.feedPreferences as Partial<FeedVisibilityPreferences>)
        : null,
    ),
    hiddenNotificationTypes: normalizeHiddenNotificationTypes(
      baseValue.hiddenNotificationTypes,
    ),
    listMetadata: nextListMetadata,
    customFeeds: nextCustomFeeds,
    notificationViewOrder,
  };
};

export const parseFocusPreferences = (
  value: unknown,
  {
    loggedIn,
    seedPreferences,
  }: {
    loggedIn: boolean;
    seedPreferences?: Partial<FeedVisibilityPreferences> | null;
  },
): FocusExtraDataV1 => {
  if (typeof value !== "string" || value.length === 0) {
    return getDefaultFocusPreferences(loggedIn, seedPreferences);
  }

  try {
    const parsed = JSON.parse(value);
    return sanitizeFocusPreferences(parsed, {
      loggedIn,
    });
  } catch {
    return getDefaultFocusPreferences(loggedIn, seedPreferences);
  }
};

export const serializeFocusPreferences = (
  preferences: FocusExtraDataV1,
  options: FocusPreferencesSanitizeOptions,
) => {
  return JSON.stringify(sanitizeFocusPreferences(preferences, options));
};

export const getFocusExtraDataString = (extraData?: unknown): string | null => {
  if (!isRecord(extraData)) {
    return null;
  }

  return typeof extraData.focus === "string" ? extraData.focus : null;
};

export const resolveFeedSurfaceOrder = (
  preferences: FocusExtraDataV1,
  options: FocusSurfaceResolutionOptions,
) => {
  return sanitizeFeedOrder(preferences.feedOrder, {
    loggedIn: options.loggedIn,
    availableListIds:
      options.availableListIds ?? options.lists?.map((list) => list.ListId),
    availableCustomFeedIds:
      options.availableCustomFeedIds ??
      Object.keys(options.customFeeds ?? preferences.customFeeds),
  });
};

export const resolveFeedSurfaceItem = (
  surface: FeedSurfaceRef,
  preferences: FocusExtraDataV1,
  options: FocusSurfaceResolutionOptions,
): FocusSurfaceResolution | null => {
  const listsById = new Map(
    (options.lists ?? []).map((list) => [list.ListId, list] as const),
  );
  const customFeeds = options.customFeeds ?? preferences.customFeeds;

  if (surface.type === "builtin") {
    if (!isBuiltinFeedId(surface.id)) {
      return null;
    }

    const builtin = BUILTIN_FEED_REGISTRY[surface.id];

    if (!options.loggedIn && builtin.loggedInOnly) {
      return null;
    }

    return {
      ref: surface,
      key: getFeedSurfaceKey(surface),
      label: builtin.label,
      type: surface.type,
      iconKey: builtin.iconKey,
    };
  }

  if (surface.type === "list") {
    const list = listsById.get(surface.id);

    if (!list) {
      return null;
    }

    return {
      ref: surface,
      key: getFeedSurfaceKey(surface),
      label: list.ListName,
      type: surface.type,
      iconKey: getFeedSurfaceIconKey(
        preferences.listMetadata[surface.id]?.iconKey,
      ),
      list,
    };
  }

  const customFeed = customFeeds[surface.id];

  if (!customFeed) {
    return null;
  }

  return {
    ref: surface,
    key: getFeedSurfaceKey(surface),
    label: customFeed.name,
    type: surface.type,
    iconKey: getFeedSurfaceIconKey(customFeed.iconKey),
    customFeed,
  };
};

export const resolveFeedSurfaceItems = (
  preferences: FocusExtraDataV1,
  options: FocusSurfaceResolutionOptions,
) => {
  return resolveFeedSurfaceOrder(
    preferences,
    options,
  ).flatMap<FocusSurfaceResolution>((surface) => {
    const resolved = resolveFeedSurfaceItem(surface, preferences, options);
    return resolved ? [resolved] : [];
  });
};
